import { faFilm, faIdCard } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select, { StylesConfig } from "react-select";
import ConditionalSpinner from "../../../components/ConditionalSpinner";
import OverlaySpinner from "../../../components/OverlaySpinner";
import useAsyncCallback from "../../../hooks/useAsyncCallback";
import useMediaSources from "../../../hooks/useMediaSources";
import { useView } from "../../../hooks/useView";
import { Run } from "../../../models/analysis/Runs";
import { DataDescription } from "../../../models/viz/dataDescriptions/DataDescription";
import { DataDescriptions } from "../../../models/viz/dataDescriptions/GenericVideoEventDataDescription";
import { getDataDescriptionsForAnalyser } from "../../../models/viz/dataDescriptions/getDataDescriptionsForAnalyser";
import { FragmentGroupDataSource } from "../../../models/viz/dataSources/FragmentGroupDataSource";
import { ApplicationState } from "../../../store";
import * as AnalyticsStore from "../../../store/analytics/Analytics";
import { AnalyticsStateView } from "../../../store/analytics/Analytics";
import { useLazyGetRunsQuery } from "../../../store/api/kinesense";
import dayjs from "dayjs";

export interface VizMediaListProps {
    selectedViewId: string;
    projectViews: AnalyticsStateView[];
    mediaId: string;
    setMediaId: (_: string) => void;
}

type VizMediaListOptionType = { value: string; label: string; type: "existing" | "new" };
type VizMediaListDisplayOption = VizMediaListOptionType & { subtitle: string };
type MediaSourceListItem = NonNullable<ReturnType<typeof useMediaSources>["allMediaSources"]>[number];

const SELECT_STYLES: StylesConfig<VizMediaListDisplayOption, false> = {
    container: (provided) => ({
        ...provided,
        // TODO: Find way to either set the width to that of the longest option, or cut off overflowing text with ellipsis, to limit size changes
        minWidth: "12rem",
    }),
    valueContainer: (provided, _state) => ({
        ...provided,
        textAlign: "center",
    }),
    menu: (base) => ({
        ...base,
        width: "max-content",
        minWidth: "100%",
    }),
};

function normaliseMediaName(name: string) {
    return name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLocaleLowerCase();
}

function formatStartsAtSubtitle(startsAt: MediaSourceListItem["startsAt"] | undefined) {
    if (startsAt === undefined) {
        return "";
    }

    const formattedDate = dayjs(startsAt);
    return formattedDate.isValid() ? formattedDate.format("YYYY-MM-DD HH:mm:ss") : "";
}

function getMediaSourcesById(mediaSources: MediaSourceListItem[]) {
    return mediaSources.reduce(
        (mediaById, mediaSource) => {
            mediaById[mediaSource.mediaId] = mediaSource;
            return mediaById;
        },
        {} as Record<string, MediaSourceListItem>,
    );
}

function getMediaIndices(mediaSources: MediaSourceListItem[]) {
    return mediaSources
        .slice()
        .sort((a, b) => a.createdAt - b.createdAt)
        .reduce(
            (indexByMediaId, mediaSource, index) => {
                indexByMediaId[mediaSource.mediaId] = index + 1;
                return indexByMediaId;
            },
            {} as Record<string, number>,
        );
}

function getMediaNameCounts(mediaSources: MediaSourceListItem[]) {
    return mediaSources.reduce(
        (countByName, mediaSource) => {
            const normalizedMediaName = normaliseMediaName(mediaSource.name);
            countByName[normalizedMediaName] = (countByName[normalizedMediaName] ?? 0) + 1;
            return countByName;
        },
        {} as Record<string, number>,
    );
}

function formatLabelWithOptionalIndex(
    label: string,
    mediaId: string,
    mediaSourcesById: Record<string, MediaSourceListItem>,
    mediaNameCounts: Record<string, number>,
    mediaIndices: Record<string, number>,
) {
    const mediaSource = mediaSourcesById[mediaId];
    if (mediaSource === undefined) {
        return label;
    }

    const shouldIncludeIndex = (mediaNameCounts[normaliseMediaName(mediaSource.name)] ?? 0) > 1;
    const mediaIndex = mediaIndices[mediaId];

    return shouldIncludeIndex && mediaIndex !== undefined ? `${label} (${mediaIndex})` : label;
}

function getExistingViewOptions(
    projectViews: AnalyticsStateView[],
    mediaSourcesById: Record<string, MediaSourceListItem>,
    mediaNameCounts: Record<string, number>,
    mediaIndices: Record<string, number>,
) {
    const existingViewOptions: VizMediaListDisplayOption[] = [];
    const usedMediaSources = new Set<string>();

    for (const view of projectViews) {
        if (!view.data?.hasAssociatedMedia || view.data.source?.type !== "frag-group") {
            continue;
        }

        const mediaId = view.data.primaryMediaId;
        usedMediaSources.add(mediaId);
        const startsAt = mediaSourcesById[mediaId]?.startsAt;

        existingViewOptions.push({
            value: view.viewId,
            label: formatLabelWithOptionalIndex(
                view.displayName ?? "???",
                mediaId,
                mediaSourcesById,
                mediaNameCounts,
                mediaIndices,
            ),
            subtitle: formatStartsAtSubtitle(startsAt),
            type: "existing",
        });
    }

    return { existingViewOptions, usedMediaSources };
}

function getNewViewOptions(
    mediaSources: MediaSourceListItem[],
    mediaRuns: Record<string, Run[]>,
    usedMediaSources: Set<string>,
    mediaSourcesById: Record<string, MediaSourceListItem>,
    mediaNameCounts: Record<string, number>,
    mediaIndices: Record<string, number>,
) {
    const newViewOptions: VizMediaListDisplayOption[] = [];

    for (const mediaSource of mediaSources) {
        const hasNonMovementData =
            mediaRuns[mediaSource.mediaId]?.find(
                (run) =>
                    getDataDescriptionsForAnalyser(run.analyserId).find(
                        (description) => description.type !== DataDescriptions.standardMovement.type,
                    ) !== undefined,
            ) !== undefined;

        if (!usedMediaSources.has(mediaSource.mediaId) && hasNonMovementData) {
            newViewOptions.push({
                value: mediaSource.mediaId,
                label: formatLabelWithOptionalIndex(
                    mediaSource.name,
                    mediaSource.mediaId,
                    mediaSourcesById,
                    mediaNameCounts,
                    mediaIndices,
                ),
                subtitle: formatStartsAtSubtitle(mediaSource.startsAt),
                type: "new",
            });
        }
    }

    return newViewOptions;
}

function VizMediaList(props: VizMediaListProps) {
    const dispatch = useDispatch();

    const { general } = useSelector((state: ApplicationState) => state);
    const projectId = general.activeProjectId;
    const { view, viewId } = useView(props.selectedViewId);

    const { allMediaSources, hasLoadedMediaSources } = useMediaSources(viewId);
    const isViewForCurrentMedia = view?.data.hasAssociatedMedia && view?.data.primaryMediaId === props.mediaId;

    const [fetchRuns] = useLazyGetRunsQuery();
    const [mediaRuns, setMediaRuns] = useState<Record<string, Run[]>>({});
    const mediaRunsRequestIdRef = useRef(0);

    useAsyncCallback(
        async () => {
            const mediaSources = allMediaSources ?? [];
            const currentRequestId = ++mediaRunsRequestIdRef.current;

            if (mediaSources.length === 0) {
                if (currentRequestId === mediaRunsRequestIdRef.current) {
                    setMediaRuns({});
                }
                return;
            }

            const newMediaRuns: typeof mediaRuns = {};
            let nextIndex = 0;
            const maxConcurrentRequests = 5;

            const worker = async () => {
                while (nextIndex < mediaSources.length) {
                    const mediaIndex = nextIndex;
                    nextIndex += 1;
                    const media = mediaSources[mediaIndex];

                    try {
                        const runs = await fetchRuns({ mediaId: media.mediaId }).unwrap();
                        newMediaRuns[media.mediaId] = runs;
                    } catch {
                        // Skip failed media source and continue processing others.
                    }
                }
            };

            await Promise.all(
                Array.from({ length: Math.min(maxConcurrentRequests, mediaSources.length) }, async () => worker()),
            );

            if (currentRequestId !== mediaRunsRequestIdRef.current) {
                return;
            }

            setMediaRuns(newMediaRuns);
        },
        [allMediaSources, hasLoadedMediaSources],
        {
            skip: !hasLoadedMediaSources,
        },
    );

    // Split available options into existing views with a media source, and then other available media sources
    const { existingViewOptions, newViewOptions } = useMemo(() => {
        const mediaSources = allMediaSources ?? [];
        const mediaSourcesById = getMediaSourcesById(mediaSources);
        const mediaIndices = getMediaIndices(mediaSources);
        const mediaNameCounts = getMediaNameCounts(mediaSources);
        const { existingViewOptions, usedMediaSources } = getExistingViewOptions(
            props.projectViews,
            mediaSourcesById,
            mediaNameCounts,
            mediaIndices,
        );
        const newViewOptions = getNewViewOptions(
            mediaSources,
            mediaRuns,
            usedMediaSources,
            mediaSourcesById,
            mediaNameCounts,
            mediaIndices,
        );

        return { existingViewOptions, newViewOptions };
    }, [props.projectViews, allMediaSources, mediaRuns]);

    const selectedOption = existingViewOptions.find((v) => v.value === viewId);

    const options = [
        { label: "Recently Viewed", options: existingViewOptions },
        { label: "All Videos", options: newViewOptions },
    ];

    function handleExistingOption(option: VizMediaListDisplayOption) {
        dispatch(AnalyticsStore.actionCreators.setPrimaryView(option.value));
        const view = props.projectViews.find((v) => v.viewId === option.value);

        if (view?.data?.hasAssociatedMedia) {
            props.setMediaId(view.data.primaryMediaId);
        }
    }

    function handleNewOption(option: VizMediaListDisplayOption) {
        const mediaSources = allMediaSources ?? [];

        dispatch(
            AnalyticsStore.actionCreators.addView(
                new FragmentGroupDataSource(
                    option.value,
                    [{ projectId: projectId, mediaId: option.value, runId: 0 }],
                    dispatch,
                ),
                true,
                mediaSources.find((m) => m.mediaId === option.value)?.name ?? "???",
                projectId,
            ),
        );

        props.setMediaId(option.value);
    }

    function onOptionChange(option: VizMediaListDisplayOption | null) {
        if (option === null) {
            return;
        }

        // NOTE: while setting the media ID alone would update/create the view, manually dispatching here
        // prevents a (sometimes) significant delay
        switch (option.type) {
            case "existing":
                handleExistingOption(option);
                break;
            case "new":
                handleNewOption(option);
                break;
        }
    }

    function renderFormatOptionLabel(option: VizMediaListDisplayOption) {
        return (
            <div className="option-label">
                <FontAwesomeIcon icon={faFilm} className="option-icon" />
                <div className="option-title">{option.label}</div>
                <div className="option-subtitle">
                    <span>{option.subtitle}</span>
                </div>
            </div>
        );
    }

    function renderDataDescriptionButton(d: DataDescription) {
        const isSelected = d.type === view?.currentDataDescription.type;

        const icon = faIdCard;
        const title = "Object Classification";

        return (
            <Button
                key={d.type}
                size="sm"
                variant="secondary"
                className={`btn-viz-data-description${isSelected ? " selected" : ""}`}
                title={title}
                onClick={() => dispatch(AnalyticsStore.actionCreators.setViewDataDescription(props.selectedViewId, d))}
            >
                <FontAwesomeIcon icon={icon} />
            </Button>
        );
    }

    const dataDescriptions =
        view?.dataDescriptions.filter((d) => d.type !== DataDescriptions.standardMovement.type) ?? [];

    return (
        <ConditionalSpinner isLoading={!hasLoadedMediaSources || viewId === undefined}>
            <div className="gap-3 d-flex">
                <div className="position-relative">
                    <Select<VizMediaListDisplayOption, false>
                        classNamePrefix="react-select"
                        options={options}
                        onChange={onOptionChange}
                        formatOptionLabel={renderFormatOptionLabel}
                        styles={SELECT_STYLES}
                        value={selectedOption}
                    />
                    <OverlaySpinner isLoading={!isViewForCurrentMedia} />
                </div>

                <AnimatePresence>
                    {dataDescriptions.length > 1 && (
                        <motion.section
                            layout
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="gap-1 d-flex"
                        >
                            {dataDescriptions.map(renderDataDescriptionButton)}
                        </motion.section>
                    )}
                </AnimatePresence>
            </div>
        </ConditionalSpinner>
    );
}

export default VizMediaList;
